import React, { useState, useEffect } from 'react';
import { Camera, Plus, Trash2, Pencil, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import BarcodeScannerModal from './BarcodeScannerModal';
import { confirmDialog } from "../ui/confirm-dialog";

const DemoUnit = ({ axiosInstance }) => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedCode, setScannedCode] = useState("");
    const [scannerMode, setScannerMode] = useState(""); // 'main_search', 'add_serial', 'edit_serial'
    
    // State for Stock Demo Unit Management
    const [demoUnits, setDemoUnits] = useState([]);
    const [isAddDemoUnitOpen, setIsAddDemoUnitOpen] = useState(false);
    const [isAddDemoUnitConfirmOpen, setIsAddDemoUnitConfirmOpen] = useState(false);
    const [isEditDemoUnitOpen, setIsEditDemoUnitOpen] = useState(false);
    const [selectedDemoUnit, setSelectedDemoUnit] = useState(null);
    const [newDemoUnits, setNewDemoUnits] = useState([{
        brand: '',
        model: '',
        itemDescription: '',
        itemCode: '',
        serialNumber: '',
        quantity: '',
        area: ''
    }]);
    const [siNumber, setSiNumber] = useState("");
    const [drNumber, setDrNumber] = useState("");
    const [supplier, setSupplier] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [activeRowIndex, setActiveRowIndex] = useState(null);

    // State for Brand Management
    const [brands, setBrands] = useState([]);
    const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");

    // State for Demo Unit Details Modal
    const [isDemoUnitDetailsOpen, setIsDemoUnitDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelDemoUnits, setModelDemoUnits] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");
    const [mainWarehouseId, setMainWarehouseId] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
            fetchDemoUnits();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            const warehouses = response.data;
            const main = warehouses.find(w => w.name === "Main Warehouse") || warehouses[0];
            if (main) {
                setMainWarehouseId(main.id);
            }
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        }
    };

    const fetchDemoUnits = async () => {
        try {
            const response = await axiosInstance.get('/stock/demo-units');
            setDemoUnits(response.data);
        } catch (error) {
            console.error("Error fetching demo units:", error);
            toast.error("Failed to fetch demo units");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/demo-units/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleScanSuccess = (code) => {
        setScannedCode(code);
        
        if (scannerMode === 'main_search') {
            setSearchFilter(code);
        } else if (scannerMode === 'add_serial') {
            if (activeRowIndex !== null) {
                const updatedDemoUnits = [...newDemoUnits];
                updatedDemoUnits[activeRowIndex].serialNumber = code;
                setNewDemoUnits(updatedDemoUnits);
            }
        } else if (scannerMode === 'edit_serial') {
            setSelectedDemoUnit(prev => ({ ...prev, serialNumber: code }));
        }

        setIsScannerOpen(false);
        setScannerMode("");
        setActiveRowIndex(null);
    };

    const openScanner = (mode, index = null) => {
        setScannerMode(mode);
        setActiveRowIndex(index);
        setIsScannerOpen(true);
    };

    const handleDemoUnitInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedDemoUnits = [...newDemoUnits];
        updatedDemoUnits[index] = {
            ...updatedDemoUnits[index],
            [name]: value
        };
        setNewDemoUnits(updatedDemoUnits);
    };

    const handleAddDemoUnitRow = () => {
        setNewDemoUnits([...newDemoUnits, {
            brand: '',
            model: '',
            itemDescription: '',
            itemCode: '',
            serialNumber: '',
            quantity: '',
            area: ''
        }]);
    };

    const handleRemoveDemoUnitRow = (index) => {
        if (newDemoUnits.length > 1) {
            const updatedDemoUnits = newDemoUnits.filter((_, i) => i !== index);
            setNewDemoUnits(updatedDemoUnits);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedDemoUnit(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveDemoUnit = async () => {
        // Basic validation
        for (const demoUnit of newDemoUnits) {
            if (!demoUnit.brand || !demoUnit.model || !demoUnit.itemCode) {
                toast.error("Please fill in required fields (Brand, Model, Item Code) for all rows");
                return;
            }
        }

        try {
            const payload = newDemoUnits.map(demoUnit => ({
                ...demoUnit,
                quantity: 1,
                si_number: siNumber,
                dr_number: drNumber,
                supplier: supplier,
                delivery_date: deliveryDate
            }));

            await axiosInstance.post('/stock/demo-units', payload);
            toast.success("Demo units added successfully");
            setIsAddDemoUnitOpen(false);
            setNewDemoUnits([{
                brand: '',
                model: '',
                itemDescription: '',
                itemCode: '',
                serialNumber: '',
                quantity: '',
                area: ''
            }]);
            setSiNumber("");
            setDrNumber("");
            setSupplier("");
            setDeliveryDate(null);
            fetchDemoUnits(); // Refresh list
        } catch (error) {
            console.error("Error saving demo units:", error);
            if (error.response && error.response.data && error.response.data.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error("Failed to save demo units");
            }
        }
    };

    const handleRowClick = (aggregatedDemoUnit) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        // Use filteredDemoUnits instead of raw demoUnits to ensure consistency
        const relatedDemoUnits = filteredDemoUnits.filter(d => 
            normalize(d.model) === normalize(aggregatedDemoUnit.model) && 
            normalize(d.brand) === normalize(aggregatedDemoUnit.brand) &&
            normalize(d.itemDescription) === normalize(aggregatedDemoUnit.itemDescription) &&
            normalize(d.itemCode) === normalize(aggregatedDemoUnit.itemCode)
        );
        setSelectedModel(aggregatedDemoUnit);
        setModelDemoUnits(relatedDemoUnits);
        setIsDemoUnitDetailsOpen(true);
    };

    const handleEditDemoUnit = (demoUnit) => {
        setSelectedDemoUnit(demoUnit);
        setIsEditDemoUnitOpen(true);
    };

    const handleDeleteDemoUnit = async (demoUnitId) => {
        const confirmed = await confirmDialog({
            title: "Delete Demo Unit",
            description: "Are you sure you want to delete this demo unit?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/demo-units/${demoUnitId}`);
            toast.success("Demo unit deleted successfully");
            
            // Refresh main list
            await fetchDemoUnits();
            
            // Update modal list
            setModelDemoUnits(prev => prev.filter(d => d.id !== demoUnitId));
            
            // If no demo units left for this model, close modal
            if (modelDemoUnits.length <= 1) {
                setIsDemoUnitDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting demo unit:", error);
            toast.error("Failed to delete demo unit");
        }
    };

    const handleUpdateDemoUnit = async () => {
        if (!selectedDemoUnit) return;
        
        try {
            const payload = {
                brand: selectedDemoUnit.brand,
                model: selectedDemoUnit.model,
                itemDescription: selectedDemoUnit.itemDescription,
                itemCode: selectedDemoUnit.itemCode,
                serialNumber: selectedDemoUnit.serialNumber,
                quantity: parseInt(selectedDemoUnit.quantity) || 0,
                area: selectedDemoUnit.area || ''
            };

            await axiosInstance.put(`/stock/demo-units/${selectedDemoUnit.id}`, payload);
            toast.success("Demo unit updated successfully");
            setIsEditDemoUnitOpen(false);
            setSelectedDemoUnit(null);
            
            // Refresh main list
            await fetchDemoUnits();
            
            // Update local state for details modal if it's open
            if (isDemoUnitDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/demo-units');
                const allDemoUnits = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelDemoUnits = allDemoUnits.filter(d => 
                    normalize(d.model) === normalize(selectedModel.model) && 
                    normalize(d.brand) === normalize(selectedModel.brand) &&
                    normalize(d.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(d.itemCode) === normalize(selectedModel.itemCode) &&
                    (!mainWarehouseId || !d.warehouse_id || d.warehouse_id === mainWarehouseId)
                );
                setModelDemoUnits(updatedModelDemoUnits);
            }
        } catch (error) {
            console.error("Error updating demo unit:", error);
            toast.error("Failed to update demo unit");
        }
    };

    const handleSaveBrand = async () => {
        if (!newBrandName.trim()) {
            toast.error("Brand name cannot be empty");
            return;
        }

        try {
            await axiosInstance.post('/stock/demo-units/brands', { name: newBrandName.trim() });
            toast.success("Brand added successfully");
            setNewBrandName("");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error saving brand:", error);
            if (error.response && error.response.status === 400) {
                toast.error("Brand already exists");
            } else {
                toast.error("Failed to save brand");
            }
        }
    };

    const handleDeleteBrand = async (brandId) => {
        try {
            await axiosInstance.delete(`/stock/demo-units/brands/${brandId}`);
            toast.success("Brand deleted successfully");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error deleting brand:", error);
            toast.error("Failed to delete brand");
        }
    };

    // Filter and Sort Demo Units
    const filteredDemoUnits = demoUnits.filter(demoUnit => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? demoUnit.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (demoUnit.itemCode?.toLowerCase().includes(searchLower) || 
             demoUnit.serialNumber?.toLowerCase().includes(searchLower));
        
        // Filter by last 24 hours (1 day recent added items)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const matchesDate = demoUnit.createdAt && new Date(demoUnit.createdAt) >= oneDayAgo;

        // Filter by Main Warehouse (exclude transferred items)
        const matchesWarehouse = !mainWarehouseId || !demoUnit.warehouse_id || demoUnit.warehouse_id === mainWarehouseId;

        return matchesBrand && matchesSearch && matchesDate && matchesWarehouse;
    });

    // Aggregate Demo Units by Brand + Model + Description + ItemCode
    const aggregatedDemoUnits = Object.values(filteredDemoUnits.reduce((acc, demoUnit) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(demoUnit.brand)}-${normalize(demoUnit.model)}-${normalize(demoUnit.itemDescription)}-${normalize(demoUnit.itemCode)}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...demoUnit,
                quantity: 0,
                count: 0
            };
        } else {
            if (acc[key].si_number !== 'Multiple' && acc[key].si_number !== demoUnit.si_number) {
                acc[key].si_number = 'Multiple';
            }
            if (acc[key].dr_number !== 'Multiple' && acc[key].dr_number !== demoUnit.dr_number) {
                acc[key].dr_number = 'Multiple';
            }
        }
        acc[key].quantity += (demoUnit.quantity || 0); 
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedDemoUnits = aggregatedDemoUnits.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.quantity - a.quantity;
            case "lowest_qty":
                return a.quantity - b.quantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Demo Units Inventory</h2>
            </div>

            {/* Split Layout: 30% Left, 70% Right */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-6">
                
                {/* Left Side: Controls */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    {/* Top Left Buttons */}
                    <div className="flex justify-start mb-4 gap-2">
                        <Button onClick={() => setIsAddDemoUnitOpen(true)} className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Demo Units
                        </Button>
                        <Button onClick={() => setIsAddBrandOpen(true)} variant="outline" className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Brand
                        </Button>
                    </div>

                    {/* Filtering Section */}
                    <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                            <Label>Brand</Label>
                            <Select value={brandFilter} onValueChange={setBrandFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Brands</SelectItem>
                                    {brands.map(brand => (
                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Search</Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Search Item Code or Serial Number..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    className="flex-1"
                                />
                                <Button 
                                    variant="outline" 
                                    size="icon" 
                                    onClick={() => openScanner('main_search')}
                                >
                                    <Camera className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Stock List Table */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Recent Activity</h3>
                        <div className="w-[200px]">
                            <Select value={sortOption} onValueChange={setSortOption}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sort By" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                                    <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                                    <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="border rounded-md flex-1 overflow-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead className="font-bold">Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Area</TableHead>
                                    <TableHead>Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedAggregatedDemoUnits.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                                            No demo units found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAggregatedDemoUnits.map((demoUnit, index) => (
                                        <TableRow 
                                            key={`${demoUnit.brand}-${demoUnit.model}-${index}`} 
                                            className="hover:bg-green-100 cursor-pointer transition-colors"
                                            onClick={() => handleRowClick(demoUnit)}
                                        >
                                            <TableCell>{demoUnit.si_number === 'Multiple' ? 'Multiple' : (demoUnit.si_number || '-')}</TableCell>
                                            <TableCell>{demoUnit.dr_number === 'Multiple' ? 'Multiple' : (demoUnit.dr_number || '-')}</TableCell>
                                            <TableCell className="font-bold">{demoUnit.brand}</TableCell>
                                            <TableCell>{demoUnit.model}</TableCell>
                                            <TableCell>{demoUnit.itemDescription}</TableCell>
                                            <TableCell>{demoUnit.itemCode}</TableCell>
                                            <TableCell>{demoUnit.area}</TableCell>
                                            <TableCell>{demoUnit.count}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Demo Unit Details Modal */}
            <Dialog open={isDemoUnitDetailsOpen} onOpenChange={setIsDemoUnitDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Demo Unit Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Total Quantity: {modelDemoUnits.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Area</TableHead>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Delivery Date</TableHead>
                                    <TableHead>Serial Number</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelDemoUnits.map((demoUnit) => (
                                    <TableRow key={demoUnit.id}>
                                        {isSuperAdmin && <TableCell>{demoUnit.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(demoUnit.createdAt).toLocaleString()}</TableCell>}
                                        <TableCell>{demoUnit.brand}</TableCell>
                                        <TableCell>{demoUnit.model}</TableCell>
                                        <TableCell>{demoUnit.itemDescription}</TableCell>
                                        <TableCell>{demoUnit.itemCode}</TableCell>
                                        <TableCell>{demoUnit.area}</TableCell>
                                        <TableCell>{demoUnit.si_number || '-'}</TableCell>
                                        <TableCell>{demoUnit.dr_number || '-'}</TableCell>
                                        <TableCell>{demoUnit.supplier || '-'}</TableCell>
                                        <TableCell>{demoUnit.delivery_date ? new Date(demoUnit.delivery_date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell className="font-medium">{demoUnit.serialNumber}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditDemoUnit(demoUnit)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteDemoUnit(demoUnit.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDemoUnitDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Demo Unit Modal */}
            <Dialog open={isAddDemoUnitOpen} onOpenChange={setIsAddDemoUnitOpen}>
                <DialogContent className="max-w-[90vw] w-full max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add New Demo Units</DialogTitle>
                        <DialogDescription>
                            Enter the details of the new demo unit stock.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-3 gap-4 p-4 border-b">
                        <div className="grid gap-2">
                            <Label htmlFor="siNumber">SI Number</Label>
                            <Input
                                id="siNumber"
                                value={siNumber}
                                onChange={(e) => setSiNumber(e.target.value)}
                                placeholder="Enter SI Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="drNumber">DR Number</Label>
                            <Input
                                id="drNumber"
                                value={drNumber}
                                onChange={(e) => setDrNumber(e.target.value)}
                                placeholder="Enter DR Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="supplier">Supplier</Label>
                            <Input
                                id="supplier"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                                placeholder="Enter Supplier"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Date of Delivery</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={"outline"}
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !deliveryDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={deliveryDate}
                                        onSelect={setDeliveryDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto py-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Area</TableHead>
                                    <TableHead>Serial No.</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {newDemoUnits.map((demoUnit, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Select 
                                                value={demoUnit.brand} 
                                                onValueChange={(value) => handleDemoUnitInputChange(index, { target: { name: 'brand', value } })}
                                            >
                                                <SelectTrigger className="w-[150px]">
                                                    <SelectValue placeholder="Brand" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {brands.map(brand => (
                                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="model"
                                                value={demoUnit.model}
                                                onChange={(e) => handleDemoUnitInputChange(index, e)}
                                                placeholder="Model"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemDescription"
                                                value={demoUnit.itemDescription}
                                                onChange={(e) => handleDemoUnitInputChange(index, e)}
                                                placeholder="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemCode"
                                                value={demoUnit.itemCode}
                                                onChange={(e) => handleDemoUnitInputChange(index, e)}
                                                placeholder="Item Code"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="area"
                                                value={demoUnit.area}
                                                onChange={(e) => handleDemoUnitInputChange(index, e)}
                                                placeholder="Area"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <Input
                                                    name="serialNumber"
                                                    value={demoUnit.serialNumber}
                                                    onChange={(e) => handleDemoUnitInputChange(index, e)}
                                                    placeholder="Serial No."
                                                />
                                                <Button 
                                                    type="button"
                                                    variant="outline" 
                                                    size="icon" 
                                                    onClick={() => openScanner('add_serial', index)}
                                                >
                                                    <Camera className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveDemoUnitRow(index)}
                                                disabled={newDemoUnits.length === 1}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleAddDemoUnitRow} variant="outline" size="sm" className="flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Add Row
                            </Button>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" onClick={() => setIsAddDemoUnitConfirmOpen(true)}>Add to Inventory</Button>
                    </DialogFooter>
                    <AlertDialog open={isAddDemoUnitConfirmOpen} onOpenChange={setIsAddDemoUnitConfirmOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Add to Inventory?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to add these items to inventory?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleSaveDemoUnit}>Confirm</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DialogContent>
            </Dialog>

            {/* Edit Demo Unit Modal */}
            <Dialog open={isEditDemoUnitOpen} onOpenChange={setIsEditDemoUnitOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Demo Unit</DialogTitle>
                        <DialogDescription>
                            Update the details of the demo unit stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedDemoUnit && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedDemoUnit.brand} 
                                        onValueChange={(value) => setSelectedDemoUnit(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedDemoUnit.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedDemoUnit.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedDemoUnit.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-area" className="text-right">
                                    Area
                                </Label>
                                <Input
                                    id="edit-area"
                                    name="area"
                                    value={selectedDemoUnit.area || ''}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-serialNumber" className="text-right">
                                    Serial No.
                                </Label>
                                <div className="col-span-3 flex gap-2">
                                    <Input
                                        id="edit-serialNumber"
                                        name="serialNumber"
                                        value={selectedDemoUnit.serialNumber}
                                        onChange={handleEditInputChange}
                                        className="flex-1"
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline" 
                                        size="icon" 
                                        onClick={() => openScanner('edit_serial')}
                                    >
                                        <Camera className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedDemoUnit.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateDemoUnit}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Brand Modal */}
            <Dialog open={isAddBrandOpen} onOpenChange={setIsAddBrandOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add New Brand</DialogTitle>
                        <DialogDescription>
                            Enter a new brand name to add to the list.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="brand-name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="brand-name"
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter brand name..."
                            />
                        </div>
                        
                        <div className="mt-4 border rounded-md p-2 max-h-[200px] overflow-y-auto">
                            <Label className="mb-2 block text-sm font-medium">Brands</Label>
                            {brands.length === 0 ? (
                                <p className="text-sm text-gray-500">No brands found.</p>
                            ) : (
                                <ul className="text-sm space-y-1">
                                    {brands.map((brand) => (
                                        <li key={brand.id} className="flex justify-between items-center text-gray-700 p-1 hover:bg-gray-100 rounded">
                                            <span>• {brand.name}</span>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteBrand(brand.id)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" onClick={handleSaveBrand}>Save Brand</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <BarcodeScannerModal 
                isOpen={isScannerOpen} 
                onClose={() => setIsScannerOpen(false)} 
                onScanSuccess={handleScanSuccess} 
            />
        </div>
    );
};

export default DemoUnit;
